import { Box, IconButton, Logo, Shimmer, ShimmerText, Text } from '@betterment/samba-react';
import { vstack } from '@betterment/samba-styled-system/patterns';
import { SambaAutoLink } from '@betterment/shared-components';

import { SidebarHeaderQuery } from './graphql/query.sidebarHeader.generated';

import { Nav } from '../../../Shared/components-v2/nav/nav';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { useProcessNavTransition } from '../../hooks/useProcessNavTransition';
import { useCollapseSidebarIfExpanded } from '../../hooks/useSidebarMode';
import { AdvisorContactLink } from '../AdvisorContactLink';

type Props = {
  loading: boolean;
  advisor: SidebarHeaderQuery['advisor'];
};

export const SidebarHeader: React.FC<Props> = ({ loading, advisor }) => {
  const collapseSidebarIfExpanded = useCollapseSidebarIfExpanded();
  const processNavTransition = useProcessNavTransition();

  if (loading) {
    return (
      <Box
        className={vstack({
          paddingTop: 'space.300',
          paddingBottom: 'space.300',
          alignItems: 'flex-start',
        })}
        data-color-mode="dark"
      >
        <Box
          css={{
            display: 'flex',
            gap: 'space.100',
            alignItems: 'center',
          }}
        >
          <Shimmer css={{ height: 'size.200', width: 'size.400', borderRadius: 'medium' }} />
          <ShimmerText textStyle="text25" css={{ width: 'size.900', flexShrink: 1 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box css={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'start' }}>
      {advisor && (
        <Box>
          {advisor.__typename === 'InstitutionalAdvisor' && (
            <Box>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <SambaAutoLink
                  aria-label="Return to Betterment Home Page"
                  to={urlBuilder.appHome.absolute()}
                  onClick={processNavTransition}
                  data-turbolinks="false"
                >
                  {advisor.logo && (
                    <Nav.Logo>
                      <img src={advisor.logo.dark} alt="Advisor firm logo" />
                    </Nav.Logo>
                  )}
                </SambaAutoLink>
                <IconButton
                  css={{ color: 'icon.onPrimary', my: 'space.300', md: { display: 'none' } }}
                  name="X"
                  label="Close navigation"
                  onClick={collapseSidebarIfExpanded}
                />
              </Box>

              <Box css={{ display: 'flex', flexDirection: 'column', mb: 'space.200' }}>
                <Nav.Text>Your advisor</Nav.Text>
                <Text color="text.onPrimary">{advisor.fullName}</Text>
              </Box>

              <Box css={{ mb: 'space.100' }}>
                {advisor.phoneNumber && (
                  <AdvisorContactLink
                    icon="Phone"
                    contactInfo={advisor.phoneNumber}
                    href={`tel:${advisor.phoneNumber}`}
                  />
                )}

                <AdvisorContactLink icon="Mail" contactInfo={advisor.email} href={`mailto:${advisor.email}`} />
              </Box>
            </Box>
          )}
          {advisor.__typename === 'PremiumAdvisor' && (
            <Box>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <SambaAutoLink
                  aria-label="Return to Betterment Home Page"
                  to={urlBuilder.appHome.absolute()}
                  onClick={processNavTransition}
                  data-turbolinks="false"
                >
                  {advisor.logo && (
                    <Nav.Logo>
                      <img src={advisor.logo.dark} alt="Betterment premium logo" />
                    </Nav.Logo>
                  )}
                </SambaAutoLink>
                <IconButton
                  css={{ color: 'icon.onPrimary', my: 'space.300', md: { display: 'none' } }}
                  name="X"
                  label="Close navigation"
                  onClick={collapseSidebarIfExpanded}
                />
              </Box>

              <Nav.Text>Talk to an advisor</Nav.Text>

              <AdvisorContactLink icon="Mail" contactInfo={advisor.email} href={`mailto:${advisor.email}`} />

              {advisor.calendarLink && (
                <AdvisorContactLink
                  icon="Calendar"
                  contactInfo="Schedule a chat"
                  href={advisor.calendarLink}
                  target="_blank"
                  rel="noreferrer"
                />
              )}
            </Box>
          )}
          <Nav.Separator />
        </Box>
      )}
      {!advisor && (
        <>
          <SambaAutoLink
            aria-label="Return to Betterment Home Page"
            to={urlBuilder.appHome.absolute()}
            onClick={processNavTransition}
            data-turbolinks="false"
          >
            <Nav.Logo>
              <Logo type="signatureWhite" css={{ height: 'size.200' }} />
            </Nav.Logo>
          </SambaAutoLink>
          <IconButton
            css={{ color: 'icon.onPrimary', my: 'space.300', md: { display: 'none' } }}
            name="X"
            label="Close navigation"
            onClick={collapseSidebarIfExpanded}
          />
        </>
      )}
    </Box>
  );
};
